import "./stationBoard.scss";
import Spinner from "components/Spinner";
import WorkflowHistoryModal, { whenText } from "components/Workflow/WorkflowHistoryModal";
import { setNotification } from "features/Notification/notificationSlice";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Modal } from "react-bootstrap";
import { useDispatch } from "react-redux";
import { Link } from "react-router-dom";
import authHeader from "services/auth-header";
import API from "services/axios";

/**
 * Station Board (user, 2026-11-02): like the Job Board, but one column per
 * station (Products -> Workflow -> Stations), each listing the products
 * waiting there -- not orders: an order with 3 products in 3 stations shows
 * in all 3. A product enters at its first station when its order is
 * Approved; Done moves it on, and once every product of the order is through
 * the order becomes Processing. Click an image to see it full size (single
 * click, user 2026-11-02).
 */

// Signed image URLs, kept for the page's life (they last 15 minutes).
const urlCache = new Map();

function Thumb({ path, onOpen }) {
  const [url, setUrl] = useState(urlCache.get(path) || null);
  useEffect(() => {
    if (!path || urlCache.has(path)) return undefined;
    let live = true;
    API.post("/art-approval/get-image", { path }, { headers: authHeader() })
      .then(({ data }) => {
        urlCache.set(path, data);
        if (live) setUrl(data);
      })
      .catch(() => {});
    return () => {
      live = false;
    };
  }, [path]);
  return (
    <div className="sb-thumb" onClick={() => url && onOpen(url)} title="Click to enlarge">
      {url ? <img src={url} alt="" /> : <i className="mdi mdi-tshirt-crew-outline" />}
    </div>
  );
}

export default function StationBoard() {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [stations, setStations] = useState([]);
  const [query, setQuery] = useState("");
  const [bigImage, setBigImage] = useState(null);
  const [lastDone, setLastDone] = useState(null);
  const [history, setHistory] = useState(null);

  const notify = useCallback(
    (message, type = "success") => dispatch(setNotification({ message, type })),
    [dispatch],
  );

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await API.get("/station-board", { headers: authHeader() });
      if (!data?.success) throw new Error(data?.message || "Failed");
      setStations(data.data?.stations || []);
    } catch (err) {
      notify(err?.response?.data?.message || err.message, "error");
    } finally {
      setLoading(false);
    }
  }, [notify]);

  useEffect(() => {
    load();
  }, [load]);

  const mark = async (item, stationName, done) => {
    try {
      setLoading(true);
      const { data } = await API.post(
        "/station-board/mark",
        { productId: item.productId, nodeId: item.nodeId, done },
        { headers: authHeader() },
      );
      if (!data?.success) throw new Error(data?.message || "Failed");
      notify(data.message);
      setLastDone(done ? { item, stationName } : null);
      await load();
    } catch (err) {
      notify(err?.response?.data?.message || err.message, "error");
      setLoading(false);
    }
  };

  const openHistory = async (item) => {
    try {
      const { data } = await API.get(`/work-order/${item.order.id}/workflows`, { headers: authHeader() });
      const product = (data?.data || []).find((p) => p.productId === item.productId);
      setHistory({
        title: `W-${item.order.woId} · #${item.productId} ${item.productStyle || ""}`,
        history: product?.history || [],
        stations: new Map((data?.stations || []).map((s) => [Number(s.id), s])),
      });
    } catch (err) {
      notify(err?.response?.data?.message || err.message, "error");
    }
  };

  const q = query.trim().toLowerCase();
  const shown = useMemo(
    () =>
      stations.map((s) => ({
        ...s,
        items: q
          ? s.items.filter((i) =>
              [`w-${i.order.woId}`, i.order.jobName, i.order.customerName, i.productStyle, String(i.productId)]
                .some((v) => String(v || "").toLowerCase().includes(q)),
            )
          : s.items,
      })),
    [stations, q],
  );

  return (
    <div className="sb-page">
      {loading && <Spinner />}
      <div className="sb-toolbar">
        <h4 className="mb-0 me-3">Workflow</h4>
        <input
          className="form-control form-control-sm"
          style={{ maxWidth: 280 }}
          placeholder="Search W-Order, job, style, product id..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        <button type="button" className="btn btn-sm bg-white border" onClick={load}>
          <i className="mdi mdi-refresh" /> Refresh
        </button>
        {lastDone && (
          <div className="sb-undo small">
            Done: W-{lastDone.item.order.woId} #{lastDone.item.productId} at {lastDone.stationName}
            <button
              type="button"
              className="btn btn-link btn-sm py-0"
              onClick={() => mark(lastDone.item, lastDone.stationName, false)}
            >
              Undo
            </button>
            <button type="button" className="btn-close ms-1" style={{ fontSize: 8 }} onClick={() => setLastDone(null)} />
          </div>
        )}
      </div>

      {!loading && !stations.length && (
        <div className="text-muted p-3">
          No stations yet. Add them in{" "}
          <Link to="/sales/products?active=stations">Products → Workflow → Stations</Link>.
        </div>
      )}

      <div className="sb-columns">
        {shown.map((s) => (
          <div key={s.id} className="sb-column">
            <div className="sb-column-head">
              <i className={`mdi mdi-${s.icon || "checkbox-blank-outline"} me-1`} />
              <b>{s.name}</b>
              <span className="sb-count">{s.items.length}</span>
            </div>
            <div className="sb-cards">
              {!s.items.length && <div className="text-muted small text-center py-3">Nothing waiting</div>}
              {s.items.map((item) => (
                <div key={`${item.productId}-${item.nodeId}`} className="sb-card">
                  <div className="d-flex gap-2">
                    <Thumb path={item.productImage} onOpen={setBigImage} />
                    <div className="flex-grow-1 min-w-0">
                      <div className="d-flex justify-content-between align-items-start">
                        <Link to={`/work-order/details/all/${item.order.woId}?page=1`} className="fw-bold">
                          W-{item.order.woId}
                        </Link>
                        <span className="badge bg-light text-dark border" title="Product ID">
                          #{item.productId}
                        </span>
                      </div>
                      {/* Job name left, customer right, half the row each,
                          cut short with "..." (user, 2026-11-02). */}
                      <div className="d-flex gap-2 sb-job">
                        <span className="text-truncate" title={item.order.jobName}>
                          {item.order.jobName || "-"}
                        </span>
                        <span className="text-truncate text-muted sb-customer" title={item.order.customerName}>
                          {item.order.customerName}
                        </span>
                      </div>
                      <div className="small text-muted text-truncate">
                        {[item.productStyle, item.productColor].filter(Boolean).join(" · ")}
                      </div>
                    </div>
                  </div>
                  <div className="d-flex align-items-center justify-content-between mt-2 small text-muted">
                    <span title="Total quantity">Qty: {item.totalQuantity || 0}</span>
                    {item.order.WoDueDate && (
                      <span title="Due">
                        <i className="mdi mdi-calendar me-1" />
                        {new Date(item.order.WoDueDate).toLocaleDateString([], { month: "short", day: "numeric" })}
                      </span>
                    )}
                    <span
                      className="text-truncate ms-2"
                      title={`${item.by ? `${item.by}, ` : ""}${whenText(item.since)}`}
                    >
                      <i className="mdi mdi-clock-outline me-1" />
                      {item.by && <b className="me-1">{item.by}</b>}
                      {whenText(item.since)}
                    </span>
                  </div>
                  <div className="d-flex gap-1 mt-2">
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-secondary"
                      title="History"
                      onClick={() => openHistory(item)}
                    >
                      <i className="mdi mdi-history" />
                    </button>
                    <button
                      type="button"
                      className="btn btn-sm btn-success flex-grow-1"
                      onClick={() => mark(item, s.name, true)}
                    >
                      <i className="mdi mdi-check" /> Done
                    </button>
                    {/* Small Undo after Done (user, 2026-11-02): undoes the
                        station before this one, sending the product back. */}
                    {item.undo && (
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary"
                        title={`Undo ${item.undo.stationName}`}
                        onClick={() => {
                          if (window.confirm(`Undo "${item.undo.stationName}" for W-${item.order.woId} #${item.productId}?`)) {
                            mark({ ...item, nodeId: item.undo.nodeId }, item.undo.stationName, false);
                          }
                        }}
                      >
                        <i className="mdi mdi-undo" />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      <Modal show={!!bigImage} onHide={() => setBigImage(null)} size="xl" centered>
        <Modal.Header closeButton>
          <Modal.Title>Product Image</Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center">
          {bigImage && <img src={bigImage} alt="" style={{ maxWidth: "100%", maxHeight: "75vh" }} />}
        </Modal.Body>
      </Modal>

      <WorkflowHistoryModal
        show={!!history}
        onHide={() => setHistory(null)}
        title={history?.title}
        history={history?.history}
        stations={history?.stations}
      />
    </div>
  );
}
